
import React, { useState, useRef } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useIsMobile } from "@/hooks/use-mobile";
import { getLeadTypeIcon, getLeadTypeColor } from '../LeadUtils';
import { LeadStatus, LeadType, LeadOutcome } from '../types/LeadTypes';

interface MultiSelectOption {
  value: string;
  label: string;
  leadType?: LeadType;
}

interface MultiSelectProps {
  id: string;
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  showIcons?: boolean;
  showColorCircles?: boolean;
  getCircleColor?: (value: string) => string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  id,
  options,
  selectedValues,
  onChange,
  placeholder = 'Select options',
  className,
  showIcons = false,
  showColorCircles = false,
  getCircleColor
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isMobile = useIsMobile();
  
  const handleSelect = (value: string) => {
    const updatedSelection = selectedValues.includes(value)
      ? selectedValues.filter(item => item !== value)
      : [...selectedValues, value];
    
    onChange(updatedSelection);
  };
  
  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };
  
  const displayValue = selectedValues.length > 0
    ? `${selectedValues.length} selected`
    : placeholder;
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          id={id}
          className={cn(
            "flex items-center justify-between gap-1.5 w-full h-10 px-3 py-1.5 text-left bg-white rounded-md border border-gray-200 focus:outline-none focus:border-gray-300 focus:ring-1 focus:ring-gray-200",
            open ? "border-gray-300" : "border-gray-200",
            className
          )}
        >
          <div className="flex items-center">
            <span className="text-sm text-gray-600">{displayValue}</span>
          </div>
          
          <div className="flex items-center">
            {selectedValues.length > 0 && (
              <X
                className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100 mr-1"
                onClick={handleClearSelection}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-full min-w-[220px] p-0 border border-gray-200 shadow-lg z-50 bg-white" align="start">
        <div className="py-0.5 max-h-60 overflow-auto">
          {options.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <div
                key={option.value}
                className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50"
                onClick={() => handleSelect(option.value)}
              >
                <label
                  className={cn(
                    "cursor-pointer flex-grow text-sm flex items-center",
                    showIcons && option.value.includes('Missed') ? "text-red-700" : ""
                  )}
                >
                  {showIcons && option.leadType && (
                    getLeadTypeIcon(option.leadType, option.value as LeadStatus)
                  )}
                  
                  {showColorCircles && getCircleColor && (
                    <div 
                      className="w-2 h-2 rounded-full mr-2" 
                      style={{ backgroundColor: getCircleColor(option.value as LeadOutcome) }} 
                    />
                  )}
                  
                  <span className="ml-1">{option.label}</span>
                </label>
                {isSelected && (
                  <Check className="h-4 w-4 text-green-600 ml-auto" />
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
